import { useEffect } from "react";
import { useConsentCookie } from "@/components/consent/cookie-consent";
import { GA_MEASUREMENT_ID, parseConsent } from "@shared/lib/consent";

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void };

/** Efface les cookies _ga / _ga_<id> sur l'hôte et ses domaines parents. */
function deleteGoogleAnalyticsCookies() {
  const names = document.cookie
    .split("; ")
    .map((entry) => entry.split("=")[0])
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const labels = window.location.hostname.split(".");
  const domains = labels.map((_, index) => labels.slice(index).join(".")).filter((domain) => domain.includes("."));
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${domain}`;
  }
}

/**
 * Google Analytics 4, chargé uniquement après accord (mode de consentement
 * « basique » : aucun appel à Google avant le choix). Un retrait d'accord en
 * cours de visite repasse le consentement à « denied » et efface les cookies.
 * Les changements de page du routeur sont suivis par la mesure améliorée de
 * GA4 (historique du navigateur).
 */
export function GoogleAnalytics() {
  const raw = useConsentCookie();
  const granted = raw !== null && parseConsent(raw)?.analytics === true;

  useEffect(() => {
    const gtag = (window as GtagWindow).gtag;
    if (!GA_MEASUREMENT_ID || !gtag) return;
    gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
    if (!granted) deleteGoogleAnalyticsCookies();
  }, [granted]);

  // Chargement unique, après accord : file gtag puis la bibliothèque.
  useEffect(() => {
    const w = window as GtagWindow & { dataLayer?: unknown[] };
    if (!GA_MEASUREMENT_ID || !granted || w.gtag) return;
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
      // gtag lit `arguments` tel quel : pas de paramètres reste ici.
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    w.gtag("js", new Date());
    w.gtag("config", GA_MEASUREMENT_ID);
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }, [granted]);

  return null;
}
